import { readPersistedAuthenticatedUserId } from '../auth/persisted-authenticated-session.ts';
import { resolveAuthenticatedReloadPreview } from '../plugin-system/authenticated-reload-preview-loader.tsx';
import type { ResolvedAuthenticatedReloadPreview } from '../plugin-system/authenticated-reload-preview-types.ts';
import LoadingFirmBrand from '../loading/FirmBrand.tsx';
import { useAuthenticatedReloadPreviewHandoffPending } from './AuthenticatedReloadPreviewHandoff.tsx';
import { parseWorkspaceLocation } from './workspace-location.ts';
import '../loading/application-loading-placeholder.css';

function currentDestination(): string {
  return typeof window === 'undefined'
    ? '/'
    : `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function AuthenticatedEntryLoadingPlaceholder() {
  const workspaceLocation = parseWorkspaceLocation(currentDestination());
  const shellHidden = workspaceLocation?.shellHidden === true;
  const primaryNavigationHidden = workspaceLocation?.primaryNavigationHidden === true;
  return (
    <div
      aria-busy="true"
      aria-label="Loading application"
      className="application-loading-placeholder application-loading-placeholder--shell"
      role="status"
    >
      {!shellHidden && !primaryNavigationHidden && (
        <aside className="application-loading-placeholder__sidebar">
          <LoadingFirmBrand />
          <span className="application-loading-placeholder__nav-line" />
          <span className="application-loading-placeholder__nav-line" />
          <span className="application-loading-placeholder__nav-line application-loading-placeholder__nav-line--short" />
        </aside>
      )}
      <main className="application-loading-placeholder__main">
        {!shellHidden && <div className="application-loading-placeholder__topbar" />}
        <div className="application-loading-placeholder__content">
          <span className="application-loading-placeholder__title" />
          <span className="application-loading-placeholder__line" />
          <span className="application-loading-placeholder__line application-loading-placeholder__line--short" />
          <div className="application-loading-placeholder__card" />
        </div>
      </main>
    </div>
  );
}

export default function AuthenticatedReloadPreviewPlaceholder({
  verifiedUserId,
}: {
  verifiedUserId?: string | null;
}) {
  const handoffPending = useAuthenticatedReloadPreviewHandoffPending();
  if (handoffPending) return null;
  const preview = resolveCurrentAuthenticatedReloadPreview(verifiedUserId);
  if (preview) return preview.content;
  return <AuthenticatedEntryLoadingPlaceholder />;
}

export function resolveCurrentAuthenticatedReloadPreview(
  verifiedUserId?: string | null,
): ResolvedAuthenticatedReloadPreview | null {
  const userId = verifiedUserId || readPersistedAuthenticatedUserId();
  if (userId) {
    const destination = currentDestination();
    const workspaceLocation = parseWorkspaceLocation(destination);
    // Cached plugin previews include shell chrome; embedded views use the compact placeholder.
    if (workspaceLocation?.shellHidden) return null;
    return resolveAuthenticatedReloadPreview({
      destination,
      primaryNavigationHidden: workspaceLocation?.primaryNavigationHidden === true,
      userId,
    });
  }
  return null;
}
